/**
 * Scan Limit Tracking Utility
 *
 * Tracks hourly scan limits for non-signed-in users using localStorage.
 * Limits: 5 scans per hour for anonymous users
 */

// Configuration
const SCAN_LIMIT_CONFIG = {
  MAX_SCANS_PER_HOUR: 5,
  TRACKING_WINDOW_MS: 60 * 60 * 1000, // 1 hour in milliseconds
  STORAGE_KEY: 'watermark_scan_limit',
  CLEANUP_INTERVAL_MS: 5 * 60 * 1000, // Cleanup every 5 minutes
};

// Type definitions
interface ScanRecord {
  timestamp: number; // Unix timestamp in milliseconds
}

interface ScanLimitData {
  scans: ScanRecord[];
  lastCleanup: number;
}

/**
 * Check if localStorage is available (SSR-safe)
 */
const isLocalStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const testKey = '__test_localStorage__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get scan limit data from localStorage
 */
const getScanLimitData = (): ScanLimitData => {
  if (!isLocalStorageAvailable()) {
    return { scans: [], lastCleanup: Date.now() };
  }

  try {
    const data = window.localStorage.getItem(SCAN_LIMIT_CONFIG.STORAGE_KEY);
    if (!data) {
      return { scans: [], lastCleanup: Date.now() };
    }

    const parsed: ScanLimitData = JSON.parse(data);

    // Validate data structure
    if (!Array.isArray(parsed.scans) || typeof parsed.lastCleanup !== 'number') {
      return { scans: [], lastCleanup: Date.now() };
    }

    return parsed;
  } catch (error) {
    console.error('[ScanLimits] Error reading localStorage:', error);
    return { scans: [], lastCleanup: Date.now() };
  }
};

/**
 * Save scan limit data to localStorage
 */
const setScanLimitData = (data: ScanLimitData): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    window.localStorage.setItem(
      SCAN_LIMIT_CONFIG.STORAGE_KEY,
      JSON.stringify(data)
    );
  } catch (error) {
    console.error('[ScanLimits] Error writing to localStorage:', error);
  }
};

/**
 * Remove scans older than the tracking window (1 hour)
 */
const cleanupOldScans = (data: ScanLimitData): ScanLimitData => {
  const now = Date.now();
  const cutoffTime = now - SCAN_LIMIT_CONFIG.TRACKING_WINDOW_MS;

  const recentScans = data.scans.filter(
    (scan) => scan.timestamp > cutoffTime
  );

  return {
    scans: recentScans,
    lastCleanup: now,
  };
};

/**
 * Check if cleanup is needed (every 5 minutes)
 */
const shouldCleanup = (data: ScanLimitData): boolean => {
  const now = Date.now();
  return (now - data.lastCleanup) > SCAN_LIMIT_CONFIG.CLEANUP_INTERVAL_MS;
};

/**
 * Track a new scan attempt
 * Automatically cleans up old scans if needed
 */
export const trackScan = (): void => {
  if (!isLocalStorageAvailable()) return;

  let data = getScanLimitData();

  // Cleanup old scans if needed
  if (shouldCleanup(data)) {
    data = cleanupOldScans(data);
  }

  // Add new scan
  data.scans.push({ timestamp: Date.now() });

  // Save to localStorage
  setScanLimitData(data);

  console.log('[ScanLimits] Scan tracked. Total scans in last hour:', data.scans.length);
};

/**
 * Get the number of remaining scans in the current hour
 */
export const getRemainingScans = (): number => {
  if (!isLocalStorageAvailable()) {
    return SCAN_LIMIT_CONFIG.MAX_SCANS_PER_HOUR; // Allow unlimited if localStorage unavailable
  }

  let data = getScanLimitData();

  // Cleanup old scans
  data = cleanupOldScans(data);

  const used = data.scans.length;
  const remaining = Math.max(0, SCAN_LIMIT_CONFIG.MAX_SCANS_PER_HOUR - used);

  return remaining;
};

/**
 * Check if the hourly scan limit has been exceeded
 */
export const isLimitExceeded = (): boolean => {
  return getRemainingScans() === 0;
};

/**
 * Get the time until the scan limit resets (oldest scan expires)
 * Returns null if no scans tracked or limit not exceeded
 */
export const getNextResetTime = (): Date | null => {
  if (!isLocalStorageAvailable()) return null;

  let data = getScanLimitData();
  data = cleanupOldScans(data);

  if (data.scans.length === 0) return null;

  // Find the oldest scan
  const oldestScan = data.scans.reduce((oldest, scan) =>
    scan.timestamp < oldest.timestamp ? scan : oldest
  );

  // Calculate when it will expire (1 hour after it was created)
  const resetTime = new Date(oldestScan.timestamp + SCAN_LIMIT_CONFIG.TRACKING_WINDOW_MS);

  return resetTime;
};

/**
 * Get minutes until the scan limit resets
 */
export const getMinutesUntilReset = (): number => {
  const resetTime = getNextResetTime();
  if (!resetTime) return 0;

  const now = Date.now();
  const diff = resetTime.getTime() - now;
  const minutes = Math.ceil(diff / (60 * 1000));

  return Math.max(0, minutes);
};

/**
 * Get the total number of scans used in the current hour
 */
export const getScansUsed = (): number => {
  if (!isLocalStorageAvailable()) return 0;

  let data = getScanLimitData();
  data = cleanupOldScans(data);

  return data.scans.length;
};

/**
 * Reset all scan tracking (useful for testing or manual reset)
 */
export const resetScanLimit = (): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    window.localStorage.removeItem(SCAN_LIMIT_CONFIG.STORAGE_KEY);
    console.log('[ScanLimits] Scan limit reset');
  } catch (error) {
    console.error('[ScanLimits] Error resetting scan limit:', error);
  }
};

/**
 * Get configuration (useful for displaying to users)
 */
export const getScanLimitConfig = () => ({
  maxScansPerHour: SCAN_LIMIT_CONFIG.MAX_SCANS_PER_HOUR,
  trackingWindowMinutes: SCAN_LIMIT_CONFIG.TRACKING_WINDOW_MS / (60 * 1000),
});
